import { useEffect, useState } from 'react'

import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'

import { FeatureFlags } from '@/helpers/featureFlags'
import type {
  FirstDibsGroup,
  FirstDibsWithWindowStart,
} from '@/queries/listingDetailsQuery'

const HOUR_MS = 60 * 60 * 1000
// setTimeout overflows above 2^31 - 1 ms (~24.8 days); re-check at least that often.
const MAX_TIMEOUT_MS = 2 ** 31 - 1

/**
 * The group's First Dibs window in epoch ms, or null when windowStart or
 * windowOffset is missing or unparseable (windowStart isn't set until the
 * listing is scheduled).
 */
export const getFdWindowMs = (
  group: FirstDibsGroup
): { startMs: number; endMs: number } | null => {
  if (!group.windowStart || group.windowOffset === undefined) return null
  const startMs = new Date(group.windowStart).getTime()
  const endMs = startMs + group.windowOffset * HOUR_MS
  return Number.isFinite(startMs) && Number.isFinite(endMs)
    ? { startMs, endMs }
    : null
}

/**
 * The group's early Buy Now price in dollars (like the auction's buyNow.price),
 * or undefined for groups that only
 * get early bidding (no early price, or a non-positive one).
 */
export const getFdEarlyPrice = (
  group: FirstDibsGroup | undefined
): number | undefined =>
  group?.earlyPrice && group.earlyPrice > 0 ? group.earlyPrice : undefined

const findActiveGroup = (groups: FirstDibsGroup[], now: number) =>
  groups.find((g) => {
    const window = getFdWindowMs(g)
    return !!window && now >= window.startMs && now < window.endMs
  })

/**
 * The buyer's group whose window is open at `now`, for code outside React
 * (e.g. building an analytics payload). Doesn't check the flag — callers do.
 */
export const getFdActiveGroup = (
  firstDibs: FirstDibsWithWindowStart | undefined,
  now: number
): FirstDibsGroup | undefined =>
  firstDibs?.enabled && firstDibs.groups?.length
    ? findActiveGroup(firstDibs.groups, now)
    : undefined

/** ms until the next window opens or closes, or null if none are ahead. */
const msUntilNextBoundary = (groups: FirstDibsGroup[], now: number) => {
  const upcoming = groups
    .flatMap((g) => {
      const window = getFdWindowMs(g)
      return window ? [window.startMs, window.endMs] : []
    })
    .filter((t) => t > now)
  return upcoming.length ? Math.min(...upcoming) - now : null
}

/**
 * Returns the buyer's active First Dibs group (if any).
 *
 * The listing service already filters firstDibs.groups to only the entries
 * the authenticated buyer is verified for, so here we only need to find
 * which of those groups has an active time window right now. Re-renders when
 * a window opens or closes so callers never act on a stale window.
 */
export function useFdActiveGroup(
  firstDibs: FirstDibsWithWindowStart | undefined
): {
  fdWindowActive: boolean
  activeGroup: FirstDibsGroup | undefined
} {
  const enableFirstDibs = useFlag(FeatureFlags.ENABLE_FIRST_DIBS)
  const groups =
    enableFirstDibs && firstDibs?.enabled ? firstDibs.groups : undefined
  // Bumped at each window boundary to re-render; the value itself is unused.
  const [boundaryTick, setBoundaryTick] = useState(0)

  useEffect(() => {
    if (!groups?.length) return
    const delay = msUntilNextBoundary(groups, Date.now())
    if (delay === null) return
    // +1ms so the timer lands just past the boundary, not on it.
    const id = setTimeout(
      () => setBoundaryTick((t) => t + 1),
      Math.min(delay + 1, MAX_TIMEOUT_MS)
    )
    return () => clearTimeout(id)
  }, [groups, boundaryTick])

  const activeGroup = groups?.length
    ? findActiveGroup(groups, Date.now()) // oxlint-disable-line react/purity
    : undefined

  return { fdWindowActive: !!activeGroup, activeGroup }
}
